import React from "react";
import { Campaign } from "../../services";

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  campaign: Campaign | null;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  campaign,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  if (!isOpen || !campaign) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Xác nhận xóa
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Hành động này không thể hoàn tác
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-4">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Bạn có chắc muốn xóa chiến dịch{" "}
            <span className="font-semibold text-gray-900 dark:text-white">
              "{campaign.name}"
            </span>{" "}
            không?
          </p>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <div className="flex justify-between mb-2">
                <span>Sản phẩm:</span>
                <span className="font-medium">{campaign.product_name}</span>
              </div>
              <div className="flex justify-between">
                <span>Trạng thái:</span>
                <span className="font-medium">
                  {campaign.status === 1
                    ? "Đang chạy"
                    : campaign.status === 2
                    ? "Đang dừng"
                    : "Đã kết thúc"}
                </span>
              </div>
            </div>
          </div>

          <p className="text-sm text-red-600 dark:text-red-400">
            ⚠️ Tất cả dữ liệu liên quan đến chiến dịch này sẽ bị xóa vĩnh viễn.
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 px-4 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Hủy
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 dark:hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isLoading && (
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {isLoading ? "Đang xóa..." : "Xóa chiến dịch"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
